import React, { useCallback, useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightMedia,
	SpotlightPrimaryAction,
} from '@atlaskit/spotlight';
import Image from '@atlaskit/image';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { SNIPPETS_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { expVal } from '@confluence/feature-experiments';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';

import snippetsSpotlightImage from './img/snippetsSpotlightImage.svg';

const i18n = defineMessages({
	snippetsTabSpotlightTitle: {
		id: 'snippets.snippets-tab-spotlight.title',
		defaultMessage: 'Save time with block templates',
		description:
			'Title for the snippets tab spotlight that introduces users to block templates in the content creation panel',
	},
	snippetsTabSpotlightBody: {
		id: 'snippets.snippets-tab-spotlight.body',
		defaultMessage: 'Save any content as a block template. Find and reuse it from the Block tab.',
		description: 'Body text for the snippets tab spotlight explaining the block templates feature',
	},
	snippetsTabSpotlightDone: {
		id: 'snippets.snippets-tab-spotlight.done',
		defaultMessage: 'Done',
		description: 'Button text to dismiss the snippets tab spotlight',
	},
});

type SnippetsTabSpotlightProps = {
	children: React.ReactNode;
};

export const SnippetsTabSpotlight = ({
	children,
}: SnippetsTabSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const { setOnboardingState } = useSetOnboardingState();

	const {
		data,
		loading: isOnboardingStateLoading,
		error: isOnboardingStateError,
	} = useGetOnboardingState(
		[SNIPPETS_ONBOARDING.HAS_SEEN_SNIPPETS_TAB_SPOTLIGHT],
		!expVal('platform_editor_blocks_conf', 'isEnabled', false),
	);

	const { hasSeenSnippetsTabSpotlight } = deserializeState(data);

	const handleSpotlightStart = useCallback(() => {
		setOnboardingState({
			key: SNIPPETS_ONBOARDING.HAS_SEEN_SNIPPETS_TAB_SPOTLIGHT,
			value: 'true',
		});

		setIsSpotlightActive(true);
	}, [setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
	}, []);

	const { useChoreographer } = useChoreographerAssets();

	const choreographer = useChoreographer('SnippetsTabSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const handleOnClick = useCallback(() => {
		void choreographer.stopMessage();
	}, [choreographer]);

	const handleOnDismiss = useCallback(() => {
		void choreographer.stopMessage();
	}, [choreographer]);

	useEffect(() => {
		if (
			!isEmbeddedConfluence &&
			!isOnboardingStateLoading &&
			!isOnboardingStateError &&
			!hasSeenSnippetsTabSpotlight &&
			expVal('platform_editor_blocks_conf', 'isEnabled', false)
		) {
			// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
			choreographer.startMessage();
		}
	}, [
		isEmbeddedConfluence,
		isOnboardingStateLoading,
		isOnboardingStateError,
		hasSeenSnippetsTabSpotlight,
		choreographer,
	]);

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				dismiss={handleOnDismiss}
				isVisible={isSpotlightActive}
				placement="bottom-start"
				offset={[0, 6]}
			>
				<SpotlightCard testId="snippets-tab-spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(i18n.snippetsTabSpotlightTitle)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightMedia>
						<Image src={snippetsSpotlightImage} alt="" />
					</SpotlightMedia>
					<SpotlightBody>
						<Text>{formatMessage(i18n.snippetsTabSpotlightBody)}</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={handleOnClick}>
								{formatMessage(i18n.snippetsTabSpotlightDone)}
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};
